import React, { useState, useEffect, useContext } from "react";
import { getFirestore, collection, getDocs, getDoc, doc, query, orderBy } from "firebase/firestore";
import { app, db } from "../firebase";
import LocationMap from "./LocationMap";
import { LocationContext } from "./LocationContext";
import { UMSSBlue } from "../utils/colors";
import {
  getUserLocation,
  processLocations
} from "../services/locationService";

const FALLBACK_SERVICE_TYPES = [
  { value: 'primary_care',  name: 'Primary Care',              label: 'Primary Care'              },
  { value: 'mental_health', name: 'Mental Health',              label: 'Mental Health'              },
  { value: 'wellness',      name: 'Wellness / Vaccines',        label: 'Wellness / Vaccines'        },
];

function Locations({ onSelect, onServiceTypeSelect }) {
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingDistances, setIsLoadingDistances] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [fetchError, setFetchError] = useState("");
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
  const { selectedLocation, setSelectedLocation, selectedServiceType, setSelectedServiceType } = useContext(LocationContext);

  useEffect(() => {
    const fetchAndProcessLocations = async () => {
      setIsLoadingLocations(true);
      setFetchError("");
      try {
        // Fetch location docs from Firestore's 'offices' collection
        const db = getFirestore(app);
        const docsRef = collection(db, "offices");
        const querySnapshot = await getDocs(docsRef);
        
        const docsData = [];
        querySnapshot.forEach((doc) => {
          docsData.push({ id: doc.id, ...doc.data() });
        });

        console.log(`Fetched ${docsData.length} locations from Firebase`);
        if (docsData.length === 0) {
          console.warn(
            "No documents were returned from the offices collection. " +
            "Check Firebase project/env config and Firestore read rules for public access."
          );
        }

        // Get user location
        setIsLoadingDistances(true);
        const userPos = await getUserLocation();
        setUserLocation(userPos);

        // Process locations (filter active, geocode addresses, calculate distances, sort)
        const processedLocations = await processLocations(docsData, userPos);
        
        console.log('Processed locations:', processedLocations.map(loc => ({
          name: loc.name || loc.id,
          distance: loc.distance?.text || 'No distance calculated',
          active: loc.active !== false ? 'Active' : 'Inactive'
        })));

        setLocations(processedLocations);
      } catch (error) {
        console.error("Error fetching and processing locations:", error);
        setFetchError(
          "Unable to load office locations from Firebase. Please refresh and try again. " +
          "If this continues, verify Firestore read permissions for the offices collection."
        );
        
        // Fallback: try to fetch locations without processing
        try {
          const db = getFirestore(app);
          const docsRef = collection(db, "offices");
          const querySnapshot = await getDocs(docsRef);
          
          const docsData = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Only include if active field is not explicitly false
            if (data.active !== false) {
              docsData.push({ id: doc.id, ...data });
            }
          });
          setLocations(docsData);
        } catch (fallbackError) {
          console.error("Fallback location fetch also failed:", fallbackError);
          setFetchError(
            "Office location lookup failed. This is usually a Firebase project configuration " +
            "or Firestore rule issue."
          );
        }
      } finally {
        setIsLoadingDistances(false);
        setIsLoadingLocations(false);
      }
    };

    fetchAndProcessLocations();
  }, []);

  // Fetch service types for the selected location from Firestore
  useEffect(() => {
    if (!selectedLocation) { setAvailableServiceTypes([]); return; }
    (async () => {
      try {
        const [officeSnap, typesSnap] = await Promise.all([
          getDoc(doc(db, 'offices', selectedLocation.id)),
          getDocs(query(collection(db, 'serviceTypes'), orderBy('sortOrder'))),
        ]);
        const allowedValues = (officeSnap.data()?.serviceTypes) || [];
        let types = typesSnap.docs
          .map(d => ({ value: d.data().value, name: d.data().name, label: d.data().name }))
          .filter(t => typesSnap.docs.find(d => d.data().value === t.value)?.data().active !== false);
        if (types.length === 0) types = FALLBACK_SERVICE_TYPES;
        if (allowedValues.length > 0) types = types.filter(t => allowedValues.includes(t.value));
        setAvailableServiceTypes(types);

        // Auto-select first type if current selection is no longer valid
        if (types.length > 0 && !types.find(t => t.value === selectedServiceType)) {
          setSelectedServiceType(types[0].value);
          if (onServiceTypeSelect) onServiceTypeSelect(types[0].value);
        }
      } catch {
        setAvailableServiceTypes(FALLBACK_SERVICE_TYPES);
      }
    })();
  }, [selectedLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle user selecting a location button
  const handleSelect = (locationObj) => {
    setSelectedLocation(locationObj); // update context
    console.log("Location ID:", locationObj.id); // Log selected location id
    onSelect(locationObj); // Pass the whole location object instead of just the id
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 500);
  };

  return (
    <div style={{ marginBottom: "1rem", fontFamily: "Quicksand, sans-serif", paddingBottom: "4rem" }}>
      {/* Inline style for spinner keyframes */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <p>Select Location:</p>
      {isLoadingLocations ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px"
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              border: "4px solid rgba(0,0,0,0.1)",
              borderTop: "4px solid #007bff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}
          ></div>
        </div>
      ) : fetchError ? (
        <div
          role="status"
          style={{
            padding: "0.75rem 1rem",
            borderRadius: "10px",
            border: "1px solid #f3c2c2",
            backgroundColor: "#fff5f5",
            color: "#9a1c1c",
            fontSize: "0.92rem",
          }}
        >
          {fetchError}
        </div>
      ) : locations.length === 0 ? (
        <div
          role="status"
          style={{
            padding: "0.75rem 1rem",
            borderRadius: "10px",
            border: "1px solid #dbe6f5",
            backgroundColor: "#f7fbff",
            color: "#375a7f",
            fontSize: "0.92rem",
          }}
        >
          No active offices found in Firebase. Make sure `offices` has documents and `active` is not set to `false`.
        </div>
      ) : (
        <>
          {isLoadingDistances && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "0.5rem",
                backgroundColor: "#f0f8ff",
                borderRadius: "8px",
                fontSize: "0.9rem",
                color: "#666"
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid rgba(0,0,0,0.1)",
                  borderTop: "2px solid #007bff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginRight: "0.5rem"
                }}
              ></div>
              Calculating distances...
            </div>
          )}
          {locations.map((loc) => (
            <button
              key={loc.id}
              onClick={() => handleSelect(loc)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "1rem",
                marginBottom: "0.5rem",
                border: selectedLocation && selectedLocation.id === loc.id ? `2px solid ${UMSSBlue}` : "1px solid #ccc",
                borderRadius: "15px",
                backgroundColor: selectedLocation && selectedLocation.id === loc.id ? "#e7f1ff" : "#fff",
                cursor: "pointer"
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontFamily: "Quicksand",
                      fontSize: "1.1rem",
                    }}
                  >
                    {loc.name || loc.id}
                  </div>
                  {loc.address && (
                    <div
                      style={{
                        fontSize: "0.9rem",
                        fontFamily: "Quicksand",
                        color: "#555"
                      }}
                    >
                      {loc.address}
                    </div>
                  )}
                  {loc.distance && loc.distance.text !== 'Unknown' && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        fontFamily: "Quicksand",
                        color: "#007bff",
                        marginTop: "0.25rem"
                      }}
                    >
                      📍 {loc.distance.text} away
                      {loc.duration && loc.duration.text !== 'Unknown' && ` • ${loc.duration.text} drive`}
                    </div>
                  )}
                </div>
                {selectedLocation && selectedLocation.id === loc.id && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill={UMSSBlue}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19l10-10-1.4-1.4z"/>
                  </svg>
                )}
              </div>
            </button>
          ))}
        </>
      )}
      {selectedLocation && (
        <div style={{
          margin: '16px 0',
          padding: '20px',
          backgroundColor: '#f7fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
        }}>
          <p style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#4a5568',
            marginBottom: '12px',
            marginTop: 0,
          }}>
            What type of care are you looking for?
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {availableServiceTypes.map(st => (
              <button
                key={st.value}
                onClick={() => {
                  setSelectedServiceType(st.value);
                  if (onServiceTypeSelect) onServiceTypeSelect(st.value);
                }}
                style={{
                  padding: '8px 20px',
                  borderRadius: '20px',
                  border: selectedServiceType === st.value ? '2px solid #4f46e5' : '1px solid #cbd5e0',
                  backgroundColor: selectedServiceType === st.value ? '#4f46e5' : '#fff',
                  color: selectedServiceType === st.value ? '#fff' : '#4a5568',
                  fontWeight: 600,
                  fontSize: '13px',
                  fontFamily: 'Quicksand, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {st.label}
              </button>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: '#a0aec0', marginTop: '8px', marginBottom: 0, fontStyle: 'italic' }}>
            Select a service to see available dates for this location.
          </p>
        </div>
      )}
      {selectedLocation && (
        <LocationMap
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />
      )}
    </div>
  );
}

export default Locations;